import { useState, useEffect } from 'react'
import {
  Link, Bell, Users, CreditCard, Globe, Check, Loader2,
  ExternalLink, Trash2, AlertCircle, ChevronRight,
} from 'lucide-react'
import { useAuthStore } from '@/core/stores/auth'
import {
  useUpdateProfile,
  useUpdateWorkspace,
  useBillingInfo,
  useCreateCheckout,
  useCreatePortal,
  useGoogleAccounts,
  useConnectGoogle,
  useDisconnectGoogle,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
  useTenantInfo,
} from '@/shared/hooks/useSettings'

// ── Reusable Section ────────────────────────────────────
function Section({
  icon: Icon,
  title,
  description,
  children,
  saving,
  saved,
}: {
  icon: React.ElementType
  title: string
  description: string
  children?: React.ReactNode
  saving?: boolean
  saved?: boolean
}) {
  return (
    <div className="bg-white rounded-card border border-ink-border p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-gold-light flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-brick" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-ink">{title}</h3>
            {saving && <Loader2 className="w-4 h-4 text-brick animate-spin" />}
            {saved && <Check className="w-4 h-4 text-green-500" />}
          </div>
          <p className="text-sm text-ink-muted mt-1">{description}</p>
          {children && <div className="mt-4">{children}</div>}
        </div>
      </div>
    </div>
  )
}

// ── Toggle ──────────────────────────────────────────────
function Toggle({
  label,
  enabled,
  onChange,
}: {
  label: string
  enabled: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-ink">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-brick' : 'bg-ink-border'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

// ── Plan Badge ──────────────────────────────────────────
function PlanBadge({ plan }: { plan: string }) {
  const colors: Record<string, string> = {
    free: 'bg-paper-warm text-ink',
    starter: 'bg-blue-50 text-blue-700',
    pro: 'bg-gold-light text-brick-dark',
    enterprise: 'bg-amber-50 text-amber-700',
  }
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors[plan] || colors.free}`}>
      {plan.charAt(0).toUpperCase() + plan.slice(1)}
    </span>
  )
}

// ══════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════

export function SettingsPage() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)

  // ── Profile ──
  const [fullName, setFullName] = useState('')
  const [profileSaved, setProfileSaved] = useState(false)
  const updateProfile = useUpdateProfile()

  useEffect(() => {
    if (user?.full_name) setFullName(user.full_name)
  }, [user])

  const handleSaveProfile = async () => {
    if (!fullName.trim()) return
    const updated = await updateProfile.mutateAsync({ full_name: fullName.trim() })
    if (updated && user) {
      setUser({ ...user, full_name: updated.full_name })
    }
    setProfileSaved(true)
    setTimeout(() => setProfileSaved(false), 2000)
  }

  // ── Workspace ──
  const { data: tenant } = useTenantInfo()
  const [workspaceName, setWorkspaceName] = useState('')
  const [workspaceSaved, setWorkspaceSaved] = useState(false)
  const updateWorkspace = useUpdateWorkspace()

  useEffect(() => {
    if (tenant?.name) setWorkspaceName(tenant.name)
  }, [tenant])

  const handleSaveWorkspace = async () => {
    if (!workspaceName.trim()) return
    await updateWorkspace.mutateAsync({ name: workspaceName.trim() })
    setWorkspaceSaved(true)
    setTimeout(() => setWorkspaceSaved(false), 2000)
  }

  // ── Billing ──
  const { data: billing } = useBillingInfo()
  const createCheckout = useCreateCheckout()
  const createPortal = useCreatePortal()

  const handleUpgrade = async (plan: string) => {
    const result = await createCheckout.mutateAsync(plan)
    if (result.checkout_url) window.location.href = result.checkout_url
  }

  const handleManageBilling = async () => {
    const result = await createPortal.mutateAsync()
    if (result.portal_url) window.location.href = result.portal_url
  }

  // ── Google Accounts ──
  const { data: googleAccounts, isLoading: accountsLoading } = useGoogleAccounts()
  const connectGoogle = useConnectGoogle()
  const disconnectGoogle = useDisconnectGoogle()

  const handleConnectGoogle = async () => {
    const result = await connectGoogle.mutateAsync()
    if (result?.auth_url) window.location.href = result.auth_url
  }

  // ── Notifications ──
  const { data: notifPrefs } = useNotificationPrefs()
  const updateNotifPrefs = useUpdateNotificationPrefs()
  const [notifs, setNotifs] = useState({
    email_alerts: true,
    email_weekly_report: true,
    inapp_alerts: true,
  })
  const [notifSaved, setNotifSaved] = useState(false)

  useEffect(() => {
    if (notifPrefs) setNotifs(notifPrefs)
  }, [notifPrefs])

  const handleSaveNotifs = async () => {
    await updateNotifPrefs.mutateAsync(notifs)
    setNotifSaved(true)
    setTimeout(() => setNotifSaved(false), 2000)
  }

  // ── URL params (google connection success) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('google') === 'connected') {
      window.history.replaceState({}, '', '/settings')
    }
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Paramètres</h1>
        <p className="text-ink-muted mt-1">Gérez votre espace de travail et vos intégrations.</p>
      </div>

      <div className="space-y-4">
        {/* ── Connecteurs (admin only) ──────────── */}
        {user?.role === 'admin' && (
          <Section
            icon={Link}
            title="Connecteurs"
            description="Gérez vos connexions Google et vos clés API (OpenAI, Claude, Gemini)."
          >
            <a
              href="/settings/connectors"
              className="inline-flex items-center gap-2 px-4 py-2 bg-onyx text-paper rounded-lg text-sm font-medium hover:bg-onyx-light transition-colors"
            >
              Gérer les connecteurs
              <ChevronRight size={14} />
            </a>
          </Section>
        )}

        {/* ── Profile ─────────────────────────────── */}
        <Section
          icon={Users}
          title="Profil"
          description="Vos informations personnelles."
          saving={updateProfile.isPending}
          saved={profileSaved}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Nom</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1">Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 rounded-lg border border-ink-border bg-paper text-sm text-ink-muted"
              />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleSaveProfile}
              disabled={updateProfile.isPending || fullName === (user?.full_name || '')}
              className="px-4 py-2 bg-brick text-white rounded-lg text-sm font-medium hover:bg-brick-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enregistrer le profil
            </button>
          </div>
        </Section>

        {/* ── Google Accounts ────────────────────── */}
        <Section
          icon={Link}
          title="Comptes Google connectés"
          description="Connectez Google Search Console et Google Business Profile pour activer les modules."
        >
          {accountsLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-faint">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : googleAccounts && googleAccounts.length > 0 ? (
            <div className="space-y-3">
              {googleAccounts.map((acc: any) => (
                <div
                  key={acc.id}
                  className="flex items-center justify-between p-3 bg-paper rounded-lg border border-ink-border"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      acc.sync_status === 'connected' ? 'bg-green-400' :
                      acc.sync_status === 'error' ? 'bg-red-400' : 'bg-amber-400'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-ink">
                        {acc.account_name || acc.account_id}
                      </p>
                      <p className="text-xs text-ink-faint">
                        {acc.account_type.toUpperCase()} · {
                          acc.last_sync
                            ? `Dernière sync: ${new Date(acc.last_sync).toLocaleDateString('fr-FR')}`
                            : 'Jamais synchronisé'
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => disconnectGoogle.mutate(acc.id)}
                    className="text-ink-faint hover:text-red-500 transition-colors"
                    title="Déconnecter"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={handleConnectGoogle}
                disabled={connectGoogle.isPending}
                className="text-sm text-brick hover:text-brick-dark font-medium"
              >
                + Connecter un autre compte
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Aucun compte Google connecté. Connectez un compte pour activer le suivi SEO et les avis.
                </p>
              </div>
              <button
                onClick={handleConnectGoogle}
                disabled={connectGoogle.isPending}
                className="px-4 py-2 bg-brick text-white rounded-lg text-sm font-medium hover:bg-brick-hover transition-colors disabled:opacity-50"
              >
                {connectGoogle.isPending ? 'Redirection…' : 'Connecter un compte Google'}
              </button>
            </div>
          )}
        </Section>

        {/* ── Notifications ──────────────────────── */}
        <Section
          icon={Bell}
          title="Notifications"
          description="Configurez les alertes email et les notifications in-app."
          saving={updateNotifPrefs.isPending}
          saved={notifSaved}
        >
          <div className="space-y-1">
            <Toggle
              label="Alertes email (pics de demande, avis négatifs)"
              enabled={notifs.email_alerts}
              onChange={(v) => setNotifs((p) => ({ ...p, email_alerts: v }))}
            />
            <Toggle
              label="Rapport hebdomadaire par email"
              enabled={notifs.email_weekly_report}
              onChange={(v) => setNotifs((p) => ({ ...p, email_weekly_report: v }))}
            />
            <Toggle
              label="Notifications in-app"
              enabled={notifs.inapp_alerts}
              onChange={(v) => setNotifs((p) => ({ ...p, inapp_alerts: v }))}
            />
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleSaveNotifs}
              disabled={updateNotifPrefs.isPending}
              className="px-4 py-2 bg-brick text-white rounded-lg text-sm font-medium hover:bg-brick-hover transition-colors disabled:opacity-50"
            >
              Enregistrer
            </button>
          </div>
        </Section>

        {/* ── Billing ────────────────────────────── */}
        <Section
          icon={CreditCard}
          title="Abonnement"
          description="Gérez votre plan et vos informations de paiement."
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-ink">Plan actuel :</span>
              <PlanBadge plan={billing?.plan || tenant?.plan || 'free'} />
            </div>

            {billing?.has_billing ? (
              <button
                onClick={handleManageBilling}
                disabled={createPortal.isPending}
                className="inline-flex items-center gap-1.5 text-sm text-brick hover:text-brick-dark font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {createPortal.isPending ? 'Redirection…' : "Gérer l'abonnement"}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpgrade('starter')}
                  disabled={createCheckout.isPending || billing?.plan === 'starter'}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Starter — 29€/mois
                </button>
                <button
                  onClick={() => handleUpgrade('pro')}
                  disabled={createCheckout.isPending || billing?.plan === 'pro'}
                  className="px-4 py-2 bg-brick text-white rounded-lg text-sm font-medium hover:bg-brick-hover transition-colors disabled:opacity-50"
                >
                  Pro — 79€/mois
                </button>
              </div>
            )}
          </div>
        </Section>

        {/* ── Workspace ──────────────────────────── */}
        <Section
          icon={Globe}
          title="Espace de travail"
          description="Paramètres généraux de votre espace de travail."
          saving={updateWorkspace.isPending}
          saved={workspaceSaved}
        >
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Nom de l'espace
            </label>
            <input
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              className="w-full max-w-md px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
            />
          </div>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleSaveWorkspace}
              disabled={updateWorkspace.isPending || workspaceName === (tenant?.name || '')}
              className="px-4 py-2 bg-brick text-white rounded-lg text-sm font-medium hover:bg-brick-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enregistrer
            </button>
          </div>
        </Section>
      </div>
    </div>
  )
}
